import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, TrendingUp, AlertTriangle, Lightbulb, X, ChevronDown, ChevronRight } from 'lucide-react';

interface Insight {
  id: string;
  type: 'anomaly' | 'trend' | 'suggestion' | 'correlation';
  title: string;
  description: string;
  confidence: number;
  severity: 'low' | 'medium' | 'high';
  data?: any;
}

interface AIInsightsPanelProps {
  data: any[];
  columns: any[];
  isVisible: boolean;
  onClose: () => void;
  className?: string;
}

export const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  data,
  columns,
  isVisible,
  onClose,
  className = ''
}) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);

  const generateInsights = React.useCallback(async () => {
    if (!data.length || !columns.length) return;

    setLoading(true);
    
    setTimeout(() => {
      const mockInsights: Insight[] = [
        {
          id: '1',
          type: 'anomaly',
          title: 'Unusual Data Pattern Detected',
          description: 'Column B shows values 3x higher than normal range in rows 15-18',
          confidence: 0.87,
          severity: 'high'
        },
        {
          id: '2',
          type: 'trend',
          title: 'Upward Trend Identified',
          description: 'Column A values have increased by 23% over the last 10 rows',
          confidence: 0.92,
          severity: 'medium'
        },
        {
          id: '3',
          type: 'suggestion',
          title: 'Formula Optimization',
          description: 'Consider using VLOOKUP instead of nested IF statements in column D',
          confidence: 0.78,
          severity: 'low'
        },
        {
          id: '4',
          type: 'correlation',
          title: 'Strong Correlation Found',
          description: 'Columns C and E show 94% positive correlation',
          confidence: 0.94,
          severity: 'medium'
        }
      ];
      
      setInsights(mockInsights);
      setLoading(false);
    }, 1000);
  }, [data, columns]);

  useEffect(() => {
    if (isVisible) {
      generateInsights();
    }
  }, [isVisible, generateInsights]);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'anomaly': return <AlertTriangle size={16} className="text-red-400" />;
      case 'trend': return <TrendingUp size={16} className="text-green-400" />;
      case 'suggestion': return <Lightbulb size={16} className="text-yellow-400" />;
      case 'correlation': return <Brain size={16} className="text-blue-400" />;
      default: return <Brain size={16} className="text-gray-400" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'border-red-500 bg-red-900/20';
      case 'medium': return 'border-yellow-500 bg-yellow-900/20';
      case 'low': return 'border-green-500 bg-green-900/20';
      default: return 'border-gray-500 bg-gray-900/20';
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className={`ai-insights-panel w-80 bg-gray-900 border-l border-gray-600 ${className}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-600">
        <div className="flex items-center gap-2">
          <Brain size={20} className="text-blue-400" />
          <h3 className="text-lg font-semibold text-white">AI Insights</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
        >
          <X size={16} className="text-gray-400" />
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            <span className="ml-2 text-gray-400">Analyzing data...</span>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {insights.map((insight) => (
                <motion.div
                  key={insight.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`border rounded-lg p-3 ${getSeverityColor(insight.severity)}`}
                >
                  <div
                    className="flex items-start gap-2 cursor-pointer"
                    onClick={() => setExpandedInsight(
                      expandedInsight === insight.id ? null : insight.id
                    )}
                  >
                    {getInsightIcon(insight.type)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-white">{insight.title}</h4>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">
                            {Math.round(insight.confidence * 100)}%
                          </span>
                          {expandedInsight === insight.id ? 
                            <ChevronDown size={14} className="text-gray-400" /> : 
                            <ChevronRight size={14} className="text-gray-400" />
                          }
                        </div>
                      </div>
                      <p className="text-xs text-slate-300 mt-1">{insight.description}</p>
                    </div>
                  </div>
                  
                  <AnimatePresence>
                    {expandedInsight === insight.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-3 pt-3 border-t border-gray-600"
                      >
                        <div className="text-xs text-gray-400">
                          <div className="mb-2">
                            <span className="font-medium">Confidence:</span> {Math.round(insight.confidence * 100)}%
                          </div>
                          <div className="mb-2">
                            <span className="font-medium">Severity:</span> {insight.severity}
                          </div>
                          <div>
                            <span className="font-medium">Recommendation:</span> Review the highlighted data points and consider the suggested actions.
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-600">
        <button
          onClick={generateInsights}
          disabled={loading}
          className="w-full px-3 py-2 bg-[#4267A1]/60 hover:bg-[#4267A1]/70 disabled:bg-gray-600 text-white text-sm rounded transition-colors"
        >
          {loading ? 'Analyzing...' : 'Refresh Insights'}
        </button>
      </div>
    </motion.div>
  );
};
